<template>
  <section
    ref="section"
    class="news-module-component">
    <div class="vfx-container">
      <div class="vfx inner-container">
        <div
          class="line-w-circle"
          :style="{
            transform:
              data.line_offset != '0' && device === 'desktop'
                ? `translateX(${data.line_offset}%)`
                : null,
          }">
          <div
            ref="line"
            class="line"
            :class="`line-l line-orange`"></div>
          <div
            class="sphere-rails"
            :class="`line-l`">
            <div
              ref="sphere"
              class="sphere" />
          </div>
        </div>
      </div>
    </div>
    <div class="inner-container">
      <div class="news">
        <div class="news-top">
          <h2
            class="news-top-title"
            v-html="data.title" />
        </div>
        <div class="top-divider" />
        <ul class="posts">
          <li
            v-for="(post, idx) in showTruncated ? truncated : posts"
            :key="`post-${idx}`"
            class="post-wrapper">
            <a
              class="post"
              :href="post.postAcf.link"
              target="_blank">
              <p
                class="post-title"
                v-html="post.title" />
              <div class="post-data">
                <div class="post-data-text">
                  <span
                    v-if="post.postAcf.source"
                    class="post-data-text-exc"
                    v-html="post.postAcf.source" />
                  <span
                    v-if="post.date"
                    class="post-data-text-date"
                    v-html="formatDate(post.date)" />
                </div>
                <span
                  v-if="post.categories.nodes.length > 0"
                  class="post-data-category"
                  v-html="post.categories.nodes[0].name" />
              </div>
              <img
                class="arrow"
                src="@/assets/img/arrow-purple.svg"
                alt="arrow-nav" />
            </a>
          </li>
        </ul>
        <div class="btn-container">
          <Button
            v-if="data.cta_button"
            :text="data.cta_button.title"
            :url="data.cta_button.url"
            theme="white-purple" />
        </div>
      </div>
    </div>
    <div class="wide-divider" />
  </section>
</template>

<script setup>
/* #META
 {
  "NAME": "NewsComponent",
  "CLASSNAME": "news-module-component",
  "URL": "http://lifemine.local/templates/"
 }
#ENDMETA */

/*
 *   Insert your logic here
 */
import { onBeforeUnmount, onMounted, ref } from 'vue'
import useGQL from '@/composables/useGQL.js'
import Button from '@/components/utility/ButtonGlobal.vue'
import useUtils from '@/composables/useUtils'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)

const { device, genUUID } = useUtils()
const animID = ref(genUUID())

const { gqlQuery } = useGQL()
const posts = ref([])
const truncated = ref([])
const showTruncated = ref(false)
const sphere = ref()
const line = ref()
const section = ref()

const getPosts = async () => {
  const pubData = await gqlQuery(`
  query getPosts {
  categories {
    nodes {
      name
    }
  }
  posts {
    nodes {
      date
      title
      postAcf {
        link
        source
      }
      categories {
        nodes {
          name
        }
      }
    }
  }
}
    `)
  //ordering items by date
  const sortedPosts = [...pubData.data.posts.nodes].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  )
  posts.value = sortedPosts
  if (posts.value.length > 4) {
    showTruncated.value = true
    truncated.value = posts.value.slice(0, 4)
  }
}

const formatDate = (date) => {
  let newDate = new Date(date)
  let options = { year: 'numeric', month: 'long', day: 'numeric' }
  return newDate.toLocaleDateString('en-US', options)
}

// const showAll = () => {
//   showTruncated.value = false
// }

const sphereAnimIn = () => {
  gsap.fromTo(
    sphere.value,
    {
      y: -150,
    },
    {
      y: 0,
      ease: 'power1.out',
      scrollTrigger: {
        trigger: section.value,
        scrub: true,
        start: 'top center+=20%',
        end: 'top+=40% center',
        // markers: true,
        id: animID.value,
      },
    }
  )
}
const lineAnim = (end) => {
  gsap.fromTo(
    line.value,
    {
      height: '0%',
    },
    {
      height: `${end ? '20' : '100'}%`,
      // ease: 'power1.out',
      scrollTrigger: {
        trigger: section.value,
        scrub: true,

        start: 'top+=100px center+=20%',
        end: `top+=400px center+=20%`,
        // markers: true,
        id: animID.value,
      },
    }
  )
}

onMounted(async () => {
  await getPosts()
  lineAnim()
  sphereAnimIn()
})

onBeforeUnmount(() => {
  const anim = ScrollTrigger.getById(animID.value)
  if (anim) anim.kill()
})

defineProps(['data'])
</script>

<style lang="scss">
.news-module-component {
  @apply relative pb-[60px] flex flex-col items-center;

  .vfx-container {
    @apply absolute w-full h-full;

    .vfx {
      @apply h-[163px] w-full;

      .line-w-circle {
        @apply relative h-full w-full;

        .line,
        .sphere-rails {
          @apply absolute top-0 w-[1px] h-full flex items-center justify-center overflow-visible;

          &-l {
            @apply left-0;
          }

          &-orange {
            @apply bg-lm_yellow;
          }

          .sphere {
            @apply min-w-[30px] min-h-[30px] rounded-full bg-lm_orange blur-[2px];
            background-color: #ff6d00;
            box-shadow: 0 0 20px #ff6d00;
          }
        }
      }
    }
  }

  .inner-container {
    .news {
      @apply border-l border-lm_orange py-[60px] lg:py-0 lg:border-l-0;

      &-top {
        @apply pb-[30px] flex items-center justify-center lg:pb-[65px] lg:pt-[60px];

        &-title {
          @apply text-4xl not-italic font-normal leading-[42px] text-center lg:heading-4 text-center;
        }
      }

      .top-divider {
        @apply w-[150%] h-px bg-lm_orange mb-[60px] lg:mb-0 lg:w-[100%] lg:absolute lg:left-0;
      }

      .posts {
        @apply pl-[22px] flex flex-col gap-16 min-h-[750px] md:min-h-[550px] lg:min-h-[475px] lg:pt-[70px] lg:gap-12 lg:ml-[5%] lg:border-l lg:border-lm_orange lg:pl-[38px] lg:pr-[30px];

        .post-wrapper {
          .post {
            @apply relative flex flex-col gap-[15px];

            &::before {
              @apply absolute -left-[31px] top-[3px] content-[''] bg-lm_orange rounded-full w-[17px] h-[17px] lg:-left-[47px];
            }

            &-title {
              @apply small lg:medium lg:max-w-[85%] xl:max-w-[995px];
            }

            &-data {
              @apply flex flex-col items-start gap-5 lg:flex-row lg:items-center lg:flex-wrap lg:max-w-[85%] xl:max-w-[995px];

              &-text {
                @apply flex flex-wrap gap-x-5 gap-y-2 lg:flex-nowrap;

                &-exc {
                  @apply caption text-lm_orange lg:small;
                }

                &-date {
                  @apply w-fit caption text-lm_orange lg:small;
                }
              }

              &-category {
                @apply bg-[#F3F0ED] small px-[25px] py-[5px] rounded-[20px];
              }
            }

            .arrow {
              @apply absolute bottom-0 right-0 lg:top-0;
            }
          }
        }
      }

      .btn-container {
        @apply mt-[45px] pl-[22px] lg:p-0 lg:self-center lg:mt-0 lg:pt-[55px] lg:flex lg:justify-center lg:ml-[5%] lg:pb-[calc(76px+60px)] lg:border-l lg:border-lm_orange;

        .button-container {
          @apply lg:absolute lg:left-0 lg:right-0 lg:mx-auto;
        }
      }
    }
  }

  .wide-divider {
    @apply w-[96.74%] h-px bg-lm_orange max-w-[1399px];
  }
}
</style>
